/**
 * OperationAssignmentsPanel
 *
 * 运营总览看板 ·「操作人员分配」（基于 wxb-ui 设计系统重做）。
 * - 以 WxbDataTable 平铺当日各操作的配人情况（批次/阶段/操作/时间/配员状态/人员）。
 * - 日期=今天或未来最近有排产的一天；‹ › 按天翻页；可与甘特选中批次联动过滤。
 * - 全部使用 wxb-ui 组件 + CSS 变量，无手写卡片/硬编码色值/emoji。
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Dayjs } from 'dayjs';
import { WxbChartShell, WxbDataTable, WxbTag, WxbButton, WxbOverlay, WxbDatePicker, WxbIcon } from '../wxb-ui';
import type { WxbTagColor } from '../wxb-ui/Tag/Tag';
import { dashboardService } from '../../services/dashboardService';
import type { DailyAssignmentsData } from '../../types/dashboard';
import './OperationAssignmentsPanel.css';

interface AssignmentRow {
    key: string;
    batchCode: string;
    stageName: string;
    operationName: string;
    operationDescription: string | null;
    time: string;
    required: number;
    assigned: number;
    names: string[];
    status: 'COMPLETE' | 'PARTIAL' | 'UNASSIGNED';
}

const STATUS_META: Record<AssignmentRow['status'], { label: string; color: WxbTagColor }> = {
    COMPLETE: { label: '配齐', color: 'green' },
    PARTIAL: { label: '部分', color: 'amber' },
    UNASSIGNED: { label: '缺人', color: 'red' },
};

const hhmm = (t?: string) => (t ? t.slice(0, 5) : '--:--');
const formatOperationDescription = (description?: string | null) => description?.trim() || '暂无描述';

interface OperationAssignmentsPanelProps {
    date: Dayjs;
    selectedBatchId?: number;
    selectedBatchCode?: string;
    /** 部门筛选：仅显示这些批次（undefined = 不限）。 */
    allowedBatchIds?: number[];
    onStepDay?: (delta: number) => void;
    onPickDate?: (d: Dayjs) => void;
}

const OperationAssignmentsPanel: React.FC<OperationAssignmentsPanelProps> = ({
    date,
    selectedBatchId,
    selectedBatchCode,
    allowedBatchIds,
    onStepDay,
    onPickDate,
}) => {
    const [data, setData] = useState<DailyAssignmentsData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);

    const dateKey = date.format('YYYY-MM-DD');
    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError(null);
        dashboardService
            .getDailyAssignments(dateKey)
            .then((res) => {
                if (!alive) return;
                setData(res);
                setError(null);
            })
            .catch(() => {
                if (!alive) return;
                setData(null);
                setError(`${dateKey} 人员分配加载失败，请稍后重试。`);
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [dateKey, reloadKey]);

    const rows = useMemo<AssignmentRow[]>(() => {
        const batches = Array.isArray(data?.batches) ? data!.batches : [];
        const allowed = allowedBatchIds ? new Set(allowedBatchIds) : null;
        const out: AssignmentRow[] = [];
        batches.forEach((b) => {
            if (selectedBatchId !== undefined && b.batch_id !== selectedBatchId) return;
            if (allowed && !allowed.has(b.batch_id)) return;
            (b.stages || []).forEach((s) => {
                (s.operations || []).forEach((op) => {
                    const names = (op.assignments || [])
                        .map((a) => a.employee_name)
                        .filter((n): n is string => !!n);
                    const required = op.required_people || 0;
                    const assigned = names.length;
                    const status: AssignmentRow['status'] =
                        assigned === 0 ? 'UNASSIGNED' : assigned >= required ? 'COMPLETE' : 'PARTIAL';
                    out.push({
                        key: `${b.batch_id}-${op.operation_plan_id}`,
                        batchCode: b.batch_code,
                        stageName: s.stage_name,
                        operationName: op.operation_name,
                        operationDescription: op.operation_description ?? null,
                        time: `${hhmm(op.start_time)}–${hhmm(op.end_time)}`,
                        required,
                        assigned,
                        names,
                        status,
                    });
                });
            });
        });
        return out.sort((a, b) => a.time.localeCompare(b.time));
    }, [data, selectedBatchId, allowedBatchIds]);

    const columns = useMemo(() => ([
        {
            title: '批次',
            dataIndex: 'batchCode',
            width: 160,
            render: (v: string) => <span className="ops-assignment-batch">{v}</span>,
        },
        { title: '阶段', dataIndex: 'stageName', width: 120 },
        {
            title: '操作',
            dataIndex: 'operationName',
            render: (_: unknown, r: AssignmentRow) => (
                <div className="ops-assignment-operation">
                    <span className="ops-assignment-operation-name">{r.operationName}</span>
                    <span className="ops-assignment-operation-description">
                        {formatOperationDescription(r.operationDescription)}
                    </span>
                </div>
            ),
        },
        {
            title: '时间',
            dataIndex: 'time',
            width: 120,
            render: (v: string) => <span className="ops-assignment-time">{v}</span>,
        },
        {
            title: '配员',
            dataIndex: 'status',
            width: 120,
            render: (_: unknown, r: AssignmentRow) => {
                const meta = STATUS_META[r.status];
                return <WxbTag color={meta.color}>{r.assigned}/{r.required} · {meta.label}</WxbTag>;
            },
        },
        {
            title: '人员',
            dataIndex: 'names',
            render: (_: unknown, r: AssignmentRow) => {
                const gap = r.required - r.assigned;
                return (
                    <div className="ops-assignment-tags">
                        {r.names.map((n, i) => (
                            <WxbTag key={i} color="neutral">{n}</WxbTag>
                        ))}
                        {gap > 0 && <WxbTag color="red">缺 {gap}</WxbTag>}
                    </div>
                );
            },
        },
    ]), []);

    const subtitle = selectedBatchCode
        ? `${dateKey} · 仅看 ${selectedBatchCode}`
        : `${dateKey} · 批次操作与人员`;

    return (
        <WxbChartShell
            icon={<WxbIcon name="users" size={16} />}
            iconColor="green"
            title="操作人员分配"
            subtitle={subtitle}
            actions={
                <div className="ops-assignment-actions">
                    <WxbDatePicker
                        value={date}
                        onChange={(d: any) => { if (d) onPickDate?.(d); }}
                        allowClear={false}
                        format="MM-DD"
                        showToday={false}
                        className="ops-assignment-date"
                    />
                    <div className="ops-assignment-nav">
                        <WxbButton
                            variant="ghost"
                            size="sm"
                            onClick={() => onStepDay?.(-1)}
                            title="上一有排产的日子"
                            className="ops-assignment-nav-btn"
                        >
                            <WxbIcon name="chevron-left" size={14} />
                        </WxbButton>
                        <WxbButton
                            variant="ghost"
                            size="sm"
                            onClick={() => onStepDay?.(1)}
                            title="下一有排产的日子"
                            className="ops-assignment-nav-btn"
                        >
                            <WxbIcon name="chevron-right" size={14} />
                        </WxbButton>
                    </div>
                </div>
            }
        >
            <WxbOverlay loading={loading}>
                <WxbDataTable<AssignmentRow>
                    rowKey="key"
                    density="compact"
                    columns={columns}
                    dataSource={error ? [] : rows}
                    pagination={false}
                    scroll={{ y: 300 }}
                    errorState={error ? {
                        title: '人员分配加载失败',
                        description: error,
                        action: (
                            <WxbButton size="sm" variant="secondary" onClick={() => setReloadKey((k) => k + 1)} disabled={loading}>
                                重试
                            </WxbButton>
                        ),
                    } : undefined}
                    emptyState={{ description: selectedBatchCode ? `${selectedBatchCode} 当日无操作` : '该日无排产操作' }}
                />
            </WxbOverlay>
        </WxbChartShell>
    );
};

export default OperationAssignmentsPanel;
